import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DayPicker, type DateRange } from "react-day-picker";
import { api } from "../api/client";
import "react-day-picker/style.css";

interface PublicHoliday {
  id: number;
  date: string;
  name: string;
  is_observed: boolean;
}

interface LeaveCalendarProps {
  regionId: number | null;
  startDate: string;
  endDate: string;
  onRangeChange: (start: string, end: string) => void;
}

const fmt = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const parse = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const DISPLAY_DATE = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
});
const DISPLAY_YEAR = new Intl.DateTimeFormat(undefined, { year: "numeric" });

export default function LeaveCalendar({
  regionId,
  startDate,
  endDate,
  onRangeChange,
}: LeaveCalendarProps) {
  const { t } = useTranslation();
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [month, setMonth] = useState(() => {
    const d = startDate ? parse(startDate) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Fetch public holidays for the current year and next year
  useEffect(() => {
    if (!regionId) return;
    const thisYear = month.getFullYear();
    void (async () => {
      try {
        const [h1, h2] = await Promise.all([
          api.get<PublicHoliday[]>(
            `/api/leave/public-holidays?region_id=${regionId}&year=${thisYear}`,
          ),
          api.get<PublicHoliday[]>(
            `/api/leave/public-holidays?region_id=${regionId}&year=${thisYear + 1}`,
          ),
        ]);
        setHolidays([...h1, ...h2]);
      } catch {
        setHolidays([]);
      }
    })();
  }, [regionId, month]);

  const holidayDates = useMemo(() => {
    const set = new Set<string>();
    for (const h of holidays) set.add(h.date);
    return set;
  }, [holidays]);

  const holidayDateObjs = useMemo(
    () => holidays.map((h) => parse(h.date)),
    [holidays],
  );

  const holidayMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holidays) m.set(h.date, h.name);
    return m;
  }, [holidays]);

  const selected: DateRange | undefined = useMemo(() => {
    if (!startDate) return undefined;
    const from = parse(startDate);
    const to = endDate ? parse(endDate) : from;
    return { from, to };
  }, [startDate, endDate]);

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) return;
    const newStart = fmt(range.from);
    const newEnd = range.to ? fmt(range.to) : newStart;
    onRangeChange(newStart, newEnd);
  };

  const businessDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const s = parse(startDate);
    const e = parse(endDate);
    if (e < s) return 0;
    let count = 0;
    const d = new Date(s);
    while (d <= e) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6 && !holidayDates.has(fmt(d))) {
        count += 1;
      }
      d.setDate(d.getDate() + 1);
    }
    return count;
  }, [startDate, endDate, holidayDates]);

  const rangeLabel = useMemo(() => {
    if (!startDate) return "";
    const s = parse(startDate);
    const e = endDate ? parse(endDate) : s;
    if (startDate === endDate || !endDate) {
      return `${DISPLAY_DATE.format(s)} ${DISPLAY_YEAR.format(s)}`;
    }
    return `${DISPLAY_DATE.format(s)} — ${DISPLAY_DATE.format(e)} ${DISPLAY_YEAR.format(e)}`;
  }, [startDate, endDate]);

  return (
    <div>
      <div className="flex justify-center">
        <DayPicker
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          disabled={{ before: today }}
          modifiers={{ holiday: holidayDateObjs }}
          modifiersClassNames={{
            holiday: "rdp-holiday",
          }}
          classNames={{
            root: "leave-calendar",
          }}
        />
      </div>

      {/* Holiday legend */}
      {startDate && endDate && (
        <div className="mt-2 space-y-1">
          {(() => {
            const s = parse(startDate);
            const e = parse(endDate);
            const matchedHolidays: string[] = [];
            const d = new Date(s);
            while (d <= e) {
              const key = fmt(d);
              const name = holidayMap.get(key);
              if (name) matchedHolidays.push(name);
              d.setDate(d.getDate() + 1);
            }
            if (matchedHolidays.length === 0) return null;
            return (
              <p className="text-xs text-amber-700">
                {t("leave.holidaysInRange", "Public holidays in range:")} {matchedHolidays.join(", ")}
              </p>
            );
          })()}
        </div>
      )}

      {/* Selected range summary */}
      {startDate && (
        <div className="mt-3 rounded-lg bg-blue-50 px-4 py-3 text-center">
          <p className="text-sm font-medium text-blue-900">{rangeLabel}</p>
          <p className="text-xs text-blue-700">
            {t("leave.businessDaysCalendar", "{{count}} business day(s)", {
              count: businessDays,
            })}
          </p>
          <button
            type="button"
            onClick={() => onRangeChange("", "")}
            className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            {t("leave.clearDates", "Clear dates")}
          </button>
        </div>
      )}

      <style>{`
        .leave-calendar {
          --rdp-accent-color: #2563eb;
          --rdp-accent-background-color: #dbeafe;
          --rdp-range_middle-background-color: #dbeafe;
          --rdp-range_middle-color: #1e3a5f;
          --rdp-day_button-width: 40px;
          --rdp-day_button-height: 40px;
          font-size: 0.875rem;
        }
        .leave-calendar .rdp-today {
          font-weight: 700;
        }
        .rdp-holiday .rdp-day_button {
          position: relative;
        }
        .rdp-holiday .rdp-day_button::after {
          content: "";
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background-color: #f59e0b;
        }
        .leave-calendar .rdp-disabled {
          opacity: 0.35;
        }
      `}</style>
    </div>
  );
}

export function useBusinessDays(
  startDate: string,
  endDate: string,
  holidayDates: Set<string>,
  halfStart: boolean,
  halfEnd: boolean,
): number {
  return useMemo(() => {
    if (!startDate || !endDate) return 0;
    const s = parse(startDate);
    const e = parse(endDate);
    if (e < s) return 0;
    let count = 0;
    const d = new Date(s);
    while (d <= e) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6 && !holidayDates.has(fmt(d))) {
        count += 1;
      }
      d.setDate(d.getDate() + 1);
    }
    if (halfStart && count > 0) count -= 0.5;
    if (halfEnd && startDate !== endDate && count > 0) count -= 0.5;
    return Math.max(0, count);
  }, [startDate, endDate, holidayDates, halfStart, halfEnd]);
}
